/**
 * SDR Stock Manager - Dynamic HTML Print & PDF Generators
 * @module utils/print
 */

import { formatExpiryDate, safeParseDate, getDaysUntilExpiry, getExpiryStatus } from './date.js';
import { formatBoxContentsSummary } from './box-contents.js';
import { escapeHtml } from './search.js';
import { compareProductNameEnThenTh } from './sorting.js';

/**
 * Safely renders HTML and triggers print dialog with full iOS Safari WebKit isolation and repeatable thead headers.
 * @param {string} [html] 
 */
export function triggerPrint(html) {
  if (typeof document === 'undefined') return;

  const content = (typeof html === 'string' && html.trim() && html.trim() !== 'undefined')
    ? html
    : (document.getElementById('printArea')?.innerHTML || '');

  if (!content || content.trim() === 'undefined' || !content.trim()) {
    console.warn('triggerPrint: No content provided to print');
    return;
  }

  const printDocumentHtml = `<!DOCTYPE html>
<html lang="th">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>พิมพ์เอกสาร - SDR Stock Manager</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 10mm 10mm 12mm 10mm;
    }
    * {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: #ffffff !important;
      color: #0f172a !important;
      font-family: 'Prompt', 'Sarabun', 'Sukhumvit Set', 'Thonburi', 'Noto Sans Thai', 'Leelawadee UI', 'Tahoma', sans-serif !important;
      font-size: 11px !important;
      line-height: 1.4 !important;
      -webkit-text-size-adjust: 100% !important;
      text-size-adjust: 100% !important;
    }
    .print-page {
      width: 100% !important;
      background: #ffffff !important;
      padding: 0 !important;
      margin: 0 !important;
    }
    .print-header {
      margin-bottom: 8px;
      padding-bottom: 6px;
      border-bottom: 2px solid #0f172a;
    }
    table.print-table {
      width: 100% !important;
      border-collapse: collapse !important;
      border-spacing: 0 !important;
      margin-top: 4px;
      display: table !important;
      page-break-inside: auto;
    }
    table.print-table thead {
      display: table-header-group !important;
    }
    table.print-table tbody {
      display: table-row-group !important;
    }
    table.print-table tr {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    table.print-table th,
    table.print-table td {
      border: 1px solid #cbd5e1 !important;
      padding: 4px 6px !important;
      text-align: left;
      font-size: 10.5px;
      position: static !important;
      vertical-align: middle;
      display: table-cell !important;
    }
    table.print-table th {
      background-color: #f1f5f9 !important;
      color: #0f172a !important;
      font-weight: 700;
      white-space: nowrap !important;
      position: static !important;
    }
    table.print-table tr:nth-child(even) td {
      background-color: #f8fafc !important;
    }
    .text-center { text-align: center !important; }
    .text-right { text-align: right !important; }
    .page-break { page-break-after: always; break-after: page; }
  </style>
</head>
<body>
  ${content}
</body>
</html>`;

  // Populate in-page printArea (strictly hidden from screen)
  let printArea = document.getElementById('printArea');
  if (!printArea) {
    printArea = document.createElement('div');
    printArea.id = 'printArea';
    printArea.className = 'print-only';
    document.body.appendChild(printArea);
  }
  printArea.style.display = 'none';
  printArea.innerHTML = content;

  // Use iframe to isolate print rendering from viewport scroll offset on iOS Safari
  let printFrame = document.getElementById('sdrPrintFrame');
  if (!printFrame) {
    printFrame = document.createElement('iframe');
    printFrame.id = 'sdrPrintFrame';
    printFrame.style.position = 'fixed';
    printFrame.style.top = '-9999px';
    printFrame.style.left = '-9999px';
    printFrame.style.width = '1024px';
    printFrame.style.height = '768px';
    printFrame.style.border = 'none';
    printFrame.style.opacity = '0';
    printFrame.style.pointerEvents = 'none';
    document.body.appendChild(printFrame);
  }

  const cleanup = () => {
    if (printArea) {
      printArea.innerHTML = '';
      printArea.style.display = 'none';
    }
  };

  try {
    const frameDoc = printFrame.contentDocument || printFrame.contentWindow.document;
    frameDoc.open();
    frameDoc.write(printDocumentHtml);
    frameDoc.close();

    try {
      printFrame.contentWindow.addEventListener('afterprint', cleanup);
    } catch (e) {}

    setTimeout(() => {
      try {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
      } catch (err) {
        window.scrollTo(0, 0);
        window.print();
      }
    }, 250);
  } catch (frameErr) {
    window.scrollTo(0, 0);
    setTimeout(() => {
      window.print();
    }, 250);
  }

  window.addEventListener('afterprint', cleanup);
  setTimeout(cleanup, 2000);
}

/**
 * Generates the full HTML markup for the Inventory Status Report (A4).
 * @param {Object|import('../types/index.js').InventoryItem[]} options
 * @param {import('../types/index.js').InventoryItem[]} [options.items]
 * @param {string} [options.locationFilter='all']
 * @param {string} [options.filter='all']
 * @param {string} [options.searchQuery='']
 * @param {string} [options.expiryWarningDate='']
 * @param {string} [options.operatorName='เจ้าหน้าที่คลัง SDR']
 * @param {Date|string} [options.printDate=new Date()]
 * @returns {string} HTML string
 */
export function generateInventoryReportHtml(options) {
  const opts = Array.isArray(options) ? { items: options } : (options || {});
  const {
    items = [],
    locationFilter = 'all',
    filter = 'all',
    searchQuery = '',
    expiryWarningDate = '',
    operatorName = 'เจ้าหน้าที่คลัง SDR',
    printDate = new Date()
  } = opts;

  let dateObj = new Date();
  if (printDate instanceof Date && !isNaN(printDate.getTime())) {
    dateObj = printDate;
  } else if (typeof printDate === 'string' && printDate.trim()) {
    const parsed = new Date(printDate);
    if (!isNaN(parsed.getTime())) {
      dateObj = parsed;
    }
  }

  const dateStr = dateObj.toLocaleDateString('th-TH', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    calendar: 'gregory'
  });
  const timeStr = dateObj.toLocaleTimeString('th-TH', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  const locationLabel = locationFilter === 'all' ? 'ทุกที่เก็บ/ตำแหน่ง' : locationFilter;

  let filterLabel = 'สินค้าทั้งหมด';
  if (filter === 'low') {
    filterLabel = 'สินค้าใกล้หมดคลัง';
  } else if (filter === 'expired') {
    filterLabel = 'สินค้าหมดอายุแล้ว';
  } else if (filter === 'near-expiry') {
    if (expiryWarningDate) {
      const parsedTarget = safeParseDate(expiryWarningDate);
      const formattedTarget = parsedTarget
        ? parsedTarget.toLocaleDateString('th-TH', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            calendar: 'gregory'
          })
        : '';
      filterLabel = `สินค้าใกล้หมดอายุ (หมดอายุก่อนวันที่ ${formattedTarget})`;
    } else {
      filterLabel = 'สินค้าใกล้หมดอายุ (ยังไม่ได้ตั้งค่าวันที่)';
    }
  }

  if (searchQuery && searchQuery.trim()) {
    filterLabel += ` [ค้นหา: "${searchQuery.trim()}"]`;
  }

  // Sort items: Group by product name alphabetically (En then Th), then by latest update date
  const sortedItems = [...items].sort((a, b) => {
    const cmp = compareProductNameEnThenTh(a.name, b.name);
    if (cmp !== 0) return cmp;
    return (b.updatedAt || '').localeCompare(a.updatedAt || '');
  });

  const totalUniqueItems = sortedItems.length;

  if (sortedItems.length === 0) {
    return `
      <div class="print-page">
        <div class="print-header">
          <div style="display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 8px;">
            <div style="text-align: left;">
              <h1 style="font-size: 20px; font-weight: 800; color: #0f172a; margin: 0; font-family: inherit;">ชมรม SDR (SDR CLUB)</h1>
              <p style="font-size: 12px; color: #475569; margin: 2px 0 0 0; font-family: inherit;">ระบบจัดการคลังสินค้าและวัสดุคงคลัง</p>
            </div>
            <div style="text-align: right;">
              <h2 style="font-size: 18px; font-weight: 700; color: #1e293b; margin: 0; font-family: inherit;">รายงานสถานะสินค้าคงคลัง</h2>
              <p style="font-size: 11px; color: #64748b; margin: 2px 0 0 0; font-family: inherit;">INVENTORY STATUS REPORT (A4)</p>
            </div>
          </div>
          <div style="border-top: 1px solid #cbd5e1; padding-top: 6px; margin-top: 4px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 11px; color: #1e293b;">
              <tr>
                <td style="width: 50%; padding: 2px 0; border: none !important;">• คลังสินค้า/ตำแหน่ง: <strong>${escapeHtml(locationLabel)}</strong></td>
                <td style="width: 50%; padding: 2px 0; border: none !important; text-align: right;">• พิมพ์เมื่อ: <strong>${escapeHtml(dateStr)} เวลา ${escapeHtml(timeStr)} น.</strong></td>
              </tr>
            </table>
          </div>
        </div>
        <table class="print-table">
          <tbody>
            <tr><td colspan="7" class="text-center" style="padding: 20px; color: #64748b; text-align: center;">ไม่พบรายการสินค้าที่ตรงกับเงื่อนไขในขณะนี้</td></tr>
          </tbody>
        </table>
      </div>
    `;
  }

  // Partition items into pages based on row height weight (Page 1 fits 18, Page 2+ fits 22)
  const maxFirstPageHeight = 18;
  const maxOtherPageHeight = 22;

  const pages = [];
  let currentPage = [];
  let currentHeight = 0;

  sortedItems.forEach(item => {
    let rowHeight = 1.0;
    if (item.name && item.name.length > 30) rowHeight += 0.35;
    if (item.notes && item.notes.trim()) rowHeight += 0.35;
    if (formatBoxContentsSummary(item.boxContents)) rowHeight += 0.35;

    const limit = pages.length === 0 ? maxFirstPageHeight : maxOtherPageHeight;
    if (currentHeight + rowHeight > limit && currentPage.length > 0) {
      pages.push(currentPage);
      currentPage = [];
      currentHeight = 0;
    }
    currentPage.push(item);
    currentHeight += rowHeight;
  });

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  const printPages = pages.length > 0 ? pages : [sortedItems];
  let fullHtml = '';
  let globalItemIndex = 0;

  printPages.forEach((pageItems, pageIdx) => {
    const isFirstPage = pageIdx === 0;
    const isLastPage = pageIdx === printPages.length - 1;

    let tableRowsHtml = '';
    pageItems.forEach(item => {
      globalItemIndex++;
      const expDateText = item.expiryDate ? formatExpiryDate(item.expiryDate) : '-';
      const boxSummaryText = formatBoxContentsSummary(item.boxContents);
      const quantityText = `${item.quantity || 0} ${item.unit || ''}`;
      const boxSuffix = boxSummaryText
        ? `<div style="font-size: 9px; color: #475569; margin-top: 2px;">ตู้/กล่องย่อย: ${escapeHtml(boxSummaryText)}</div>`
        : '';
      const noteSuffix =
        item.notes && item.notes.trim()
          ? `<div style="font-size: 9px; color: #64748b; font-style: italic; margin-top: 2px;">หมายเหตุ: ${escapeHtml(item.notes)}</div>`
          : '';

      tableRowsHtml += `
        <tr>
          <td class="text-center" style="text-align: center; white-space: nowrap;">${globalItemIndex}</td>
          <td>
            <div style="font-weight: 600; color: #0f172a;">${escapeHtml(item.name || '')}</div>
            ${boxSuffix}${noteSuffix}
          </td>
          <td>${escapeHtml(item.brand || '-')}</td>
          <td class="text-center" style="text-align: center; white-space: nowrap;">${escapeHtml(item.size || '-')}</td>
          <td class="text-center" style="text-align: center; white-space: nowrap;">${escapeHtml(item.lot || '-')}</td>
          <td class="text-center" style="text-align: center; white-space: nowrap;">${escapeHtml(expDateText)}</td>
          <td class="text-right" style="text-align: right; font-weight: 700; color: #0f172a; white-space: nowrap;">${escapeHtml(quantityText)}</td>
        </tr>
      `;
    });

    const pageHeaderHtml = isFirstPage
      ? `
      <div class="print-header">
        <div style="display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 8px;">
          <div style="text-align: left;">
            <h1 style="font-size: 20px; font-weight: 800; color: #0f172a; margin: 0; font-family: inherit;">ชมรม SDR (SDR CLUB)</h1>
            <p style="font-size: 12px; color: #475569; margin: 2px 0 0 0; font-family: inherit;">ระบบจัดการคลังสินค้าและวัสดุคงคลัง</p>
          </div>
          <div style="text-align: right;">
            <h2 style="font-size: 18px; font-weight: 700; color: #1e293b; margin: 0; font-family: inherit;">รายงานสถานะสินค้าคงคลัง</h2>
            <p style="font-size: 11px; color: #64748b; margin: 2px 0 0 0; font-family: inherit;">INVENTORY STATUS REPORT (A4)</p>
          </div>
        </div>
        
        <div style="border-top: 1px solid #cbd5e1; padding-top: 6px; margin-top: 4px;">
          <table style="width: 100%; border-collapse: collapse; font-size: 11px; color: #1e293b;">
            <tr>
              <td style="width: 50%; padding: 2px 0; border: none !important;">• คลังสินค้า/ตำแหน่ง: <strong>${escapeHtml(locationLabel)}</strong></td>
              <td style="width: 50%; padding: 2px 0; border: none !important; text-align: right;">• พิมพ์เมื่อ: <strong>${escapeHtml(dateStr)} เวลา ${escapeHtml(timeStr)} น.</strong></td>
            </tr>
            <tr>
              <td style="padding: 2px 0; border: none !important;">• ตัวกรองข้อมูล: <strong>${escapeHtml(filterLabel)}</strong></td>
              <td style="padding: 2px 0; border: none !important; text-align: right;">• ระบบงาน: <strong>คลังสินค้า ชมรม SDR</strong></td>
            </tr>
          </table>
        </div>
      </div>
      `
      : `
      <div class="print-header" style="margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #cbd5e1;">
        <div style="display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: #475569;">
          <span><strong>ชมรม SDR</strong> — รายงานสถานะสินค้าคงคลัง (ต่อ)</span>
          <span>คลัง: <strong>${escapeHtml(locationLabel)}</strong> | หน้า <strong>${pageIdx + 1}</strong> จาก <strong>${printPages.length}</strong></span>
        </div>
      </div>
      `;

    const summaryFooterHtml = isLastPage
      ? `
      <div style="margin-top: 12px; border: 1px solid #cbd5e1; background: #f8fafc; border-radius: 6px; padding: 8px 12px; display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: #1e293b; page-break-inside: avoid; break-inside: avoid;">
        <span>จำนวนรายการรวม: <strong>${totalUniqueItems}</strong> รายการ</span>
        <span>ผู้จัดทำเอกสาร: <strong>${escapeHtml(operatorName)}</strong></span>
      </div>
      `
      : '';

    fullHtml += `
      <div class="print-page" style="page-break-after: ${isLastPage ? 'auto' : 'always'}; break-after: ${isLastPage ? 'auto' : 'page'};">
        ${pageHeaderHtml}
        <table class="print-table">
          <thead>
            <tr>
              <th style="width: 6%; text-align: center; white-space: nowrap;">ลำดับ</th>
              <th style="width: 32%; text-align: left; white-space: nowrap;">รายการสินค้า</th>
              <th style="width: 14%; text-align: left; white-space: nowrap;">แบรนด์</th>
              <th style="width: 12%; text-align: center; white-space: nowrap;">ขนาด</th>
              <th style="width: 12%; text-align: center; white-space: nowrap;">ล็อต</th>
              <th style="width: 12%; text-align: center; white-space: nowrap;">หมดอายุ</th>
              <th style="width: 12%; text-align: right; white-space: nowrap;">คงเหลือ</th>
            </tr>
          </thead>
          <tbody>
            ${tableRowsHtml}
          </tbody>
        </table>
        ${summaryFooterHtml}
      </div>
    `;
  });

  return fullHtml;
}

/**
 * Generates the full HTML markup for the Picking Guide with automatic A4 page splitting.
 * @param {Object|import('../types/index.js').PickingAllocation[]} options
 * @param {import('../types/index.js').PickingAllocation[]} [options.pickingAllocations]
 * @param {string} [options.borrowerName='']
 * @param {Date|string} [options.printDate=new Date()]
 * @returns {string} HTML string
 */
export function generatePickingGuideHtml(options) {
  const opts = Array.isArray(options) ? { pickingAllocations: options } : (options || {});
  const { pickingAllocations = [], borrowerName = '', printDate = new Date() } = opts;

  const maxFirstPageHeight = 12;
  const maxOtherPageHeight = 17;

  // Group allocations into pages based on estimated row height
  const pages = [];
  let currentPage = [];
  let currentHeight = 0;

  pickingAllocations.forEach(alloc => {
    let rowHeight = 1.0;
    if (alloc.itemName && alloc.itemName.length > 28) {
      rowHeight += 0.5 * Math.floor(alloc.itemName.length / 28);
    }

    const brandSizeParts = [];
    if (alloc.brand) brandSizeParts.push(alloc.brand);
    if (alloc.size) brandSizeParts.push(alloc.size);
    if (brandSizeParts.length > 0) rowHeight += 0.4;
    if (formatBoxContentsSummary(alloc.boxContents)) rowHeight += 0.4;

    const limit = pages.length === 0 ? maxFirstPageHeight : maxOtherPageHeight;
    if (currentHeight + rowHeight > limit && currentPage.length > 0) {
      pages.push(currentPage);
      currentPage = [];
      currentHeight = 0;
    }
    currentPage.push(alloc);
    currentHeight += rowHeight;
  });

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  const printPages = pages.length > 0 ? pages : [pickingAllocations];
  let printHtml = '';

  let dateObj = new Date();
  if (printDate instanceof Date && !isNaN(printDate.getTime())) {
    dateObj = printDate;
  } else if (typeof printDate === 'string' && printDate.trim()) {
    const parsed = new Date(printDate);
    if (!isNaN(parsed.getTime())) {
      dateObj = parsed;
    }
  }

  const printDateStr =
    dateObj.toLocaleDateString('th-TH', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      calendar: 'gregory'
    }) +
    ' เวลา ' +
    dateObj.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit', hour12: false }) +
    ' น.';

  const totalPickItems = pickingAllocations.length;
  let totalPickQty = 0;
  pickingAllocations.forEach(alloc => {
    totalPickQty += Number(alloc.quantity || 0);
  });

  printPages.forEach((pageCards, pageIdx) => {
    let tableRows = '';
    let startItemIndex = 0;
    for (let p = 0; p < pageIdx; p++) {
      startItemIndex += printPages[p].length;
    }

    pageCards.forEach((alloc, itemIdx) => {
      const globalIndex = startItemIndex + itemIdx + 1;
      const brandSizeParts = [];
      if (alloc.brand) brandSizeParts.push(alloc.brand);
      if (alloc.size) brandSizeParts.push(alloc.size);
      const brandSizeLabel = brandSizeParts.length > 0 ? brandSizeParts.join(' / ') : '';
      const allocBoxSummaryLabel = formatBoxContentsSummary(alloc.boxContents);

      const itemDetailHtml = [
        brandSizeLabel ? `<div style="font-size: 10px; color: #475569; margin-top: 2px;">แบรนด์/ขนาด: ${escapeHtml(brandSizeLabel)}</div>` : '',
        allocBoxSummaryLabel ? `<div style="font-size: 10px; color: #475569; margin-top: 2px;">${escapeHtml(allocBoxSummaryLabel)}</div>` : ''
      ].join('');

      let expiryText = 'ไม่มีวันหมดอายุ';
      if (alloc.expiryDate) {
        expiryText = formatExpiryDate(alloc.expiryDate);
      }

      const safeAllocQty = Number.isFinite(Number(alloc.quantity)) ? String(Number(alloc.quantity)) : escapeHtml(String(alloc.quantity || '0'));

      tableRows += `
        <tr>
          <td class="text-center" style="text-align: center;">${globalIndex}</td>
          <td>
            <div style="font-weight: 600; color: #0f172a;">${escapeHtml(alloc.itemName)}</div>
            ${itemDetailHtml}
          </td>
          <td class="text-center" style="text-align: center; font-weight: 700;">${escapeHtml(alloc.location || '-')}</td>
          <td class="text-center" style="text-align: center;">${escapeHtml(alloc.lot || '-')}</td>
          <td class="text-center" style="text-align: center; font-size: 10px;">${escapeHtml(expiryText)}</td>
          <td class="text-right" style="text-align: right; font-weight: 800; color: #0f172a;">${safeAllocQty} ${escapeHtml(alloc.unit || '')}</td>
          <td class="text-center" style="text-align: center; font-size: 17px; line-height: 1; font-family: inherit;">☐</td>
        </tr>
      `;
    });

    const headerHtml =
      pageIdx === 0
        ? `
      <div class="print-header" style="border-bottom: 2px solid #0f172a; padding-bottom: 12px; margin-bottom: 15px; text-align: left;">
        <div style="display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 8px;">
          <div style="text-align: left;">
            <h1 style="font-size: 21px; font-weight: 800; color: #0f172a; margin: 0; font-family: inherit;">ชมรม SDR (SDR CLUB)</h1>
            <p style="font-size: 12px; color: #475569; margin: 2px 0 0 0; font-family: inherit;">ระบบจัดการคลังสินค้าและวัสดุคงคลัง</p>
          </div>
          <div style="text-align: right;">
            <h2 style="font-size: 19px; font-weight: 700; color: #10b981; margin: 0; font-family: inherit;">ใบสั่งจัดเตรียมสินค้า (Picking Guide)</h2>
            <p style="font-size: 11px; color: #64748b; margin: 2px 0 0 0; font-family: inherit;">วันที่พิมพ์: ${escapeHtml(printDateStr)}</p>
          </div>
        </div>
        <div style="border-top: 1px solid #94a3b8; padding-top: 8px; margin-top: 4px;">
          <table style="width: 100%; border-collapse: collapse; font-size: 11px; color: #1e293b;">
            <tr>
              <td style="width: 50%; padding: 3px 0; border: none !important;">• ผู้ยืม/ผู้ขอเบิก: <strong>${escapeHtml(borrowerName || 'สมาชิก SDR')}</strong></td>
              <td style="width: 50%; padding: 3px 0; border: none !important; text-align: right;">• จำนวนรายการรวม: <strong>${totalPickItems} รายการ</strong></td>
            </tr>
            <tr>
              <td style="padding: 3px 0; border: none !important;">• จุดประสงค์: <strong>จัดของตามใบคำขอขอยืม/จ่ายสินค้า</strong></td>
              <td style="padding: 3px 0; border: none !important; text-align: right;">• จำนวนสินค้ารวม: <strong>${totalPickQty.toLocaleString('th-TH')} หน่วย</strong></td>
            </tr>
          </table>
        </div>
      </div>
    `
        : '';

    printHtml += `
      <div class="print-page" style="page-break-after: ${pageIdx === printPages.length - 1 ? 'auto' : 'always'}; break-after: ${pageIdx === printPages.length - 1 ? 'auto' : 'page'};">
        ${headerHtml}
        <table class="print-table" style="margin-top: 0;">
          <thead>
            <tr style="background-color: #f1f5f9;">
              <th style="width: 8%; text-align: center;">ลำดับ</th>
              <th style="text-align: left;">รายการสินค้า</th>
              <th style="width: 15%; text-align: center;">ที่เก็บ</th>
              <th style="width: 10%; text-align: center;">ล็อต</th>
              <th style="width: 12%; text-align: center;">หมดอายุ</th>
              <th style="width: 13%; text-align: right;">จำนวน</th>
              <th style="width: 10%; text-align: center;">ตรวจ</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
        <div class="print-footer" style="margin-top: 15px; display: flex; justify-content: space-between; font-size: 11px; color: #64748b;">
          <span>ใบจัดเตรียมสินค้า - ชมรม SDR</span>
          <span>หน้า ${pageIdx + 1} จาก ${printPages.length}</span>
        </div>
      </div>
    `;
  });

  return printHtml;
}

/**
 * Generates the full HTML markup for the Stock Out Voucher (A4).
 * @param {Object} options
 * @param {import('../types/index.js').StockOutMaster} options.master
 * @param {import('../types/index.js').StockOutDetail[]} options.details
 * @param {import('../types/index.js').InventoryItem[]} [options.inventoryItems=[]]
 * @param {Date|string} [options.printDate=new Date()]
 * @returns {string} HTML string
 */
export function generateStockOutReportHtml(options) {
  if (!options || !options.master) return '';
  const { master, details = [], inventoryItems = [], printDate = new Date() } = options;

  // Sort details alphabetically (A-Z then ก-ฮ)
  const sortedDetails = [...details].sort((a, b) => compareProductNameEnThenTh(a.itemName, b.itemName));

  const txDate = safeParseDate(master.timestamp || master.date || master.createdAt) || new Date();
  const formattedDate = txDate.toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    calendar: 'gregory'
  });

  let dateObj = new Date();
  if (printDate instanceof Date && !isNaN(printDate.getTime())) {
    dateObj = printDate;
  } else if (typeof printDate === 'string' && printDate.trim()) {
    const parsed = new Date(printDate);
    if (!isNaN(parsed.getTime())) {
      dateObj = parsed;
    }
  }

  const printDateFormatted = dateObj.toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    calendar: 'gregory'
  }) + ' เวลา ' + dateObj.toLocaleTimeString('th-TH', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  const maxFirstPageHeight = 10;
  const maxOtherPageHeight = 15;

  const pages = [];
  let currentPage = [];
  let currentHeight = 0;

  sortedDetails.forEach(item => {
    let rowHeight = 1.0;
    if (item.itemName && item.itemName.length > 28) {
      rowHeight += 0.5 * Math.floor(item.itemName.length / 28);
    }

    const limit = pages.length === 0 ? maxFirstPageHeight : maxOtherPageHeight;
    if (currentHeight + rowHeight > limit && currentPage.length > 0) {
      pages.push(currentPage);
      currentPage = [];
      currentHeight = 0;
    }
    currentPage.push(item);
    currentHeight += rowHeight;
  });

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  const printPages = pages.length > 0 ? pages : [sortedDetails];
  let printHtml = '';

  printPages.forEach((pageItems, pageIdx) => {
    let tableRows = '';
    let startItemIndex = 0;
    for (let p = 0; p < pageIdx; p++) {
      startItemIndex += printPages[p].length;
    }

    pageItems.forEach((d, itemIdx) => {
      const globalIndex = startItemIndex + itemIdx + 1;
      const matchedItem = inventoryItems.find(i => i && i.id === d.itemId) || {};
      const brand = matchedItem.brand || '-';
      const size = matchedItem.size || '-';
      const lot = matchedItem.lot || '-';
      const expiryText = matchedItem.expiryDate ? formatExpiryDate(matchedItem.expiryDate) : '-';

      const boxSummaryText = formatBoxContentsSummary(matchedItem.boxContents);
      const boxSuffix = boxSummaryText
        ? `<div style="font-size: 10px; color: #475569; margin-top: 2px;">ตู้/กล่องย่อย: ${escapeHtml(boxSummaryText)}</div>`
        : '';
      const noteSuffix =
        matchedItem.notes && matchedItem.notes.trim()
          ? `<div style="font-size: 10px; color: #475569; font-style: italic; margin-top: 2px;">หมายเหตุ: ${escapeHtml(matchedItem.notes)}</div>`
          : '';

      const safeDetailQty = Number.isFinite(Number(d.quantity)) ? String(Number(d.quantity)) : escapeHtml(String(d.quantity || '0'));

      tableRows += `
        <tr>
          <td class="text-center" style="text-align: center;">${globalIndex}</td>
          <td>${escapeHtml(d.itemName)}${boxSuffix}${noteSuffix}</td>
          <td>${escapeHtml(brand)}</td>
          <td class="text-center" style="text-align: center;">${escapeHtml(size)}</td>
          <td class="text-center" style="text-align: center;">${escapeHtml(lot)}</td>
          <td class="text-center" style="text-align: center;">${escapeHtml(expiryText)}</td>
          <td class="text-right" style="text-align: right; font-weight: 700;">${safeDetailQty} ${escapeHtml(d.unit || matchedItem.unit || '')}</td>
        </tr>
      `;
    });

    const headerHtml =
      pageIdx === 0
        ? `
      <div class="print-header" style="border-bottom: 2px solid #0f172a; padding-bottom: 12px; margin-bottom: 15px; text-align: left;">
        <div style="display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 8px;">
          <div style="text-align: left;">
            <h1 style="font-size: 21px; font-weight: 800; color: #0f172a; margin: 0; font-family: inherit;">ชมรม SDR (SDR CLUB)</h1>
            <p style="font-size: 12px; color: #475569; margin: 2px 0 0 0; font-family: inherit;">ระบบจัดการคลังสินค้าและวัสดุคงคลัง</p>
          </div>
          <div style="text-align: right;">
            <h2 style="font-size: 19px; font-weight: 700; color: #ef4444; margin: 0; font-family: inherit;">ใบจ่ายสินค้า (Stock Out Voucher)</h2>
            <p style="font-size: 11px; color: #64748b; margin: 2px 0 0 0; font-family: inherit;">พิมพ์เมื่อ: ${escapeHtml(printDateFormatted)} น.</p>
          </div>
        </div>
        <div style="border-top: 1px solid #94a3b8; padding-top: 8px; margin-top: 4px;">
          <table style="width: 100%; border-collapse: collapse; font-size: 11px; color: #1e293b;">
            <tr>
              <td style="width: 50%; padding: 3px 0; border: none !important;">• ผู้จ่าย/ดำเนินการ: <strong>${escapeHtml(master.operatorName || master.operator || master.operator_name || '-')}</strong></td>
              <td style="width: 50%; padding: 3px 0; border: none !important; text-align: right;">• วันที่ทำรายการ: <strong>${escapeHtml(formattedDate)}</strong></td>
            </tr>
            <tr>
              <td style="padding: 3px 0; border: none !important;">• เหตุผลการจ่ายออก: <strong>${escapeHtml(master.reason || '-')}</strong></td>
              <td style="padding: 3px 0; border: none !important; text-align: right;">• จำนวนรายการรวม: <strong>${sortedDetails.length} รายการ</strong></td>
            </tr>
          </table>
        </div>
      </div>
    `
        : '';

    printHtml += `
      <div class="print-page" style="page-break-after: ${pageIdx === printPages.length - 1 ? 'auto' : 'always'}; break-after: ${pageIdx === printPages.length - 1 ? 'auto' : 'page'};">
        ${headerHtml}
        <table class="print-table" style="margin-top: 0;">
          <thead>
            <tr style="background-color: #f1f5f9;">
              <th style="width: 8%; text-align: center;">ลำดับ</th>
              <th style="text-align: left;">รายการสินค้า</th>
              <th style="width: 12%; text-align: left;">แบรนด์</th>
              <th style="width: 10%; text-align: center;">ขนาด</th>
              <th style="width: 10%; text-align: center;">ล็อต</th>
              <th style="width: 12%; text-align: center;">หมดอายุ</th>
              <th style="width: 12%; text-align: right;">จำนวน</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
        <div class="print-footer" style="margin-top: 15px; display: flex; justify-content: space-between; font-size: 11px; color: #64748b;">
          <span>ใบจ่ายสินค้า - ชมรม SDR</span>
          <span>หน้า ${pageIdx + 1} จาก ${printPages.length}</span>
        </div>
      </div>
    `;
  });

  return printHtml;
}

export const generateStockOutVoucherHtml = generateStockOutReportHtml;

/**
 * Generates printable A4 barcode and asset tags for inventory items.
 * @param {import('../types/index.js').InventoryItem[]|Object} itemsOrOptions
 * @returns {string} HTML markup
 */
export function generateBarcodeLabelsHtml(itemsOrOptions) {
  const items = Array.isArray(itemsOrOptions) ? itemsOrOptions : (itemsOrOptions?.items || []);

  const sortedItems = [...items].sort((a, b) => compareProductNameEnThenTh(a.name, b.name));

  if (sortedItems.length === 0) {
    return `
      <div class="print-page" style="padding: 40px; text-align: center; color: #64748b;">
        <h2>ไม่พบรายการสินค้าสำหรับพิมพ์ป้าย Barcode/QR</h2>
      </div>
    `;
  }

  // Deterministic SVG pseudo-barcode pattern generator
  const generateSvgBarcode = (code) => {
    const cleanCode = String(code || 'SDR-ITEM').toUpperCase().replace(/[^A-Z0-9-]/g, '');
    let bars = '';
    let x = 0;
    for (let i = 0; i < cleanCode.length; i++) {
      const charVal = cleanCode.charCodeAt(i);
      const w1 = (charVal % 3) + 1;
      const s1 = ((charVal >> 1) % 2) + 1;
      const w2 = ((charVal >> 2) % 3) + 1;
      const s2 = ((charVal >> 3) % 2) + 1;
      bars += `<rect x="${x}" y="0" width="${w1}" height="24" fill="#0f172a" />`;
      x += w1 + s1;
      bars += `<rect x="${x}" y="0" width="${w2}" height="24" fill="#0f172a" />`;
      x += w2 + s2;
    }
    return `
      <svg viewBox="0 0 ${Math.max(x, 100)} 24" style="width: 100%; height: 24px; display: block;" preserveAspectRatio="none">
        ${bars}
      </svg>
    `;
  };

  const labelsHtml = sortedItems.map((item) => {
    const lotCode = item.lot || 'LOT-MAIN';
    const expDate = item.expiryDate ? formatExpiryDate(item.expiryDate) : 'ไม่มีวันหมดอายุ';
    const sizeBrandParts = [item.brand, item.size].filter(Boolean).join(' • ');

    return `
      <div style="border: 1px dashed #94a3b8; border-radius: 6px; padding: 6px 8px; background: #ffffff; display: flex; flex-direction: column; justify-content: space-between; page-break-inside: avoid; break-inside: avoid; height: 110px; box-sizing: border-box; overflow: hidden;">
        <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e2e8f0; padding-bottom: 2px; margin-bottom: 3px;">
          <span style="font-size: 8.5px; font-weight: 800; color: #4338ca; text-transform: uppercase;">SDR STOCK</span>
          <span style="font-size: 8px; color: #64748b;">${escapeHtml(item.location || '-')}</span>
        </div>
        <div style="flex: 1; min-height: 0;">
          <div style="font-size: 10.5px; font-weight: 700; color: #0f172a; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
            ${escapeHtml(item.name || 'พัสดุ')}
          </div>
          ${sizeBrandParts ? `<div style="font-size: 8.5px; color: #475569; margin-top: 1px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHtml(sizeBrandParts)}</div>` : ''}
          <div style="display: flex; justify-content: space-between; font-size: 8px; color: #64748b; margin-top: 2px;">
            <span>ล็อต: <strong>${escapeHtml(lotCode)}</strong></span>
            <span>EXP: <strong>${escapeHtml(expDate)}</strong></span>
          </div>
        </div>
        <div style="margin-top: 3px; padding-top: 2px; border-top: 1px dashed #e2e8f0; text-align: center;">
          ${generateSvgBarcode(item.id || lotCode)}
          <div style="font-size: 7.5px; color: #64748b; font-family: monospace; letter-spacing: 0.5px; margin-top: 1px;">
            ${escapeHtml(item.id || lotCode)}
          </div>
        </div>
      </div>
    `;
  }).join('');

  return `
    <div class="print-page" style="padding: 0;">
      <style>
        .labels-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 6mm;
          width: 100%;
        }
      </style>
      <div class="labels-grid">
        ${labelsHtml}
      </div>
    </div>
  `;
}

/**
 * Generates printable A4 Physical Inventory Count & Audit Sheet.
 * @param {Object} options
 * @param {import('../types/index.js').InventoryItem[]} options.items
 * @param {Record<string, number>} [options.counts={}]
 * @param {string} [options.locationFilter='all']
 * @param {string} [options.operatorName='เจ้าหน้าที่ตรวจนับ']
 * @param {Date|string} [options.printDate=new Date()]
 * @returns {string} HTML markup
 */
export function generateAuditSheetHtml(options = {}) {
  const {
    items = [],
    counts = {},
    locationFilter = 'all',
    operatorName = 'เจ้าหน้าที่ตรวจนับ',
    printDate = new Date()
  } = options;

  let dateObj = new Date();
  if (printDate instanceof Date && !isNaN(printDate.getTime())) {
    dateObj = printDate;
  } else if (typeof printDate === 'string' && printDate.trim()) {
    const parsed = new Date(printDate);
    if (!isNaN(parsed.getTime())) {
      dateObj = parsed;
    }
  }

  const dateStr = dateObj.toLocaleDateString('th-TH', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    calendar: 'gregory'
  });
  const timeStr = dateObj.toLocaleTimeString('th-TH', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  const locationLabel = locationFilter === 'all' ? 'ทุกสถานที่ / ตู้จัดเก็บ' : locationFilter;

  // Calculate discrepancy stats
  let totalDiscrepancies = 0;
  items.forEach(item => {
    const sys = Number(item.quantity) || 0;
    const phys = counts[item.id] !== undefined ? Number(counts[item.id]) : sys;
    if (Math.abs(phys - sys) > 0.0001) totalDiscrepancies++;
  });

  const maxFirstPageHeight = 15;
  const maxOtherPageHeight = 20;

  const pages = [];
  let currentPage = [];
  let currentHeight = 0;

  items.forEach(item => {
    let rowHeight = 1.0;
    if (item.name && item.name.length > 30) rowHeight += 0.35;
    const limit = pages.length === 0 ? maxFirstPageHeight : maxOtherPageHeight;
    if (currentHeight + rowHeight > limit && currentPage.length > 0) {
      pages.push(currentPage);
      currentPage = [];
      currentHeight = 0;
    }
    currentPage.push(item);
    currentHeight += rowHeight;
  });

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  const printPages = pages.length > 0 ? pages : [items];
  let fullHtml = '';
  let globalIndex = 0;

  printPages.forEach((pageItems, pageIdx) => {
    const isFirstPage = pageIdx === 0;
    const isLastPage = pageIdx === printPages.length - 1;

    let tableRows = '';
    pageItems.forEach(item => {
      globalIndex++;
      const sys = Number(item.quantity) || 0;
      const phys = counts[item.id] !== undefined ? Number(counts[item.id]) : sys;
      const diff = Number((phys - sys).toFixed(5));

      let diffStyle = 'color: #059669; font-weight: 700;';
      let diffText = '0';
      if (diff > 0) {
        diffStyle = 'color: #0891b2; font-weight: 700;';
        diffText = `+${diff}`;
      } else if (diff < 0) {
        diffStyle = 'color: #dc2626; font-weight: 700;';
        diffText = `${diff}`;
      }

      tableRows += `
        <tr>
          <td class="text-center" style="text-align: center; white-space: nowrap;">${globalIndex}</td>
          <td>
            <div style="font-weight: 600; color: #0f172a;">${escapeHtml(item.name || '')}</div>
            <div style="font-size: 9px; color: #0891b2; font-weight: 600;">📍 ${escapeHtml(item.location || '-')}</div>
          </td>
          <td>${escapeHtml(item.brand || '-')}</td>
          <td class="text-center" style="text-align: center; white-space: nowrap;">${escapeHtml(item.size || '-')}</td>
          <td class="text-center" style="text-align: center; white-space: nowrap;">${escapeHtml(item.lot || 'ล็อตหลัก')}</td>
          <td class="text-right" style="text-align: right; font-weight: 700; color: #4338ca; white-space: nowrap;">${sys} ${escapeHtml(item.unit || '')}</td>
          <td class="text-center" style="text-align: center; font-weight: 800; color: #0f172a; white-space: nowrap;">${phys}</td>
          <td class="text-right" style="text-align: right; white-space: nowrap; ${diffStyle}">${diffText}</td>
        </tr>
      `;
    });

    const pageHeaderHtml = isFirstPage ? `
      <div class="print-header">
        <div style="display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 8px;">
          <div style="text-align: left;">
            <h1 style="font-size: 20px; font-weight: 800; color: #0f172a; margin: 0; font-family: inherit;">ชมรม SDR (SDR CLUB)</h1>
            <p style="font-size: 12px; color: #475569; margin: 2px 0 0 0; font-family: inherit;">ระบบจัดการคลังสินค้าและวัสดุคงคลัง</p>
          </div>
          <div style="text-align: right;">
            <h2 style="font-size: 18px; font-weight: 700; color: #4338ca; margin: 0; font-family: inherit;">ใบบันทึกผลตรวจนับสต็อก</h2>
            <p style="font-size: 11px; color: #64748b; margin: 2px 0 0 0; font-family: inherit;">PHYSICAL INVENTORY AUDIT SHEET</p>
          </div>
        </div>
        <div style="border-top: 1px solid #cbd5e1; padding-top: 6px; margin-top: 4px;">
          <table style="width: 100%; border-collapse: collapse; font-size: 11px; color: #1e293b;">
            <tr>
              <td style="width: 50%; padding: 2px 0; border: none !important;">• สถานที่จัดเก็บ: <strong>${escapeHtml(locationLabel)}</strong></td>
              <td style="width: 50%; padding: 2px 0; border: none !important; text-align: right;">• วันที่ตรวจนับ: <strong>${escapeHtml(dateStr)} เวลา ${escapeHtml(timeStr)} น.</strong></td>
            </tr>
            <tr>
              <td style="padding: 2px 0; border: none !important;">• ผู้ตรวจนับ: <strong>${escapeHtml(operatorName)}</strong></td>
              <td style="padding: 2px 0; border: none !important; text-align: right;">• จำนวนรายการ: <strong>${items.length} รายการ</strong> (ผลต่าง ${totalDiscrepancies} รายการ)</td>
            </tr>
          </table>
        </div>
      </div>
    ` : `
      <div class="print-header" style="margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #cbd5e1;">
        <div style="display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: #475569;">
          <span><strong>ชมรม SDR</strong> — ใบบันทึกผลตรวจนับสต็อก (ต่อ)</span>
          <span>คลัง: <strong>${escapeHtml(locationLabel)}</strong> | หน้า <strong>${pageIdx + 1}</strong> จาก <strong>${printPages.length}</strong></span>
        </div>
      </div>
    `;

    const summaryFooterHtml = isLastPage ? `
      <div style="margin-top: 16px; border: 1px solid #cbd5e1; background: #f8fafc; border-radius: 6px; padding: 12px 16px; display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: #1e293b; page-break-inside: avoid; break-inside: avoid;">
        <div style="width: 45%; text-align: center; border-right: 1px dashed #cbd5e1; padding-right: 12px;">
          <div style="margin-bottom: 28px;">ลงชื่อ..............................................................</div>
          <div>( ${escapeHtml(operatorName)} )</div>
          <div style="color: #64748b; font-size: 10px; margin-top: 2px;">ผู้ตรวจนับสต็อก</div>
        </div>
        <div style="width: 45%; text-align: center; padding-left: 12px;">
          <div style="margin-bottom: 28px;">ลงชื่อ..............................................................</div>
          <div>(..............................................................)</div>
          <div style="color: #64748b; font-size: 10px; margin-top: 2px;">หัวหน้าฝ่ายคลัง/ผู้รับรองการกระทบยอด</div>
        </div>
      </div>
    ` : '';

    fullHtml += `
      <div class="print-page" style="page-break-after: ${isLastPage ? 'auto' : 'always'}; break-after: ${isLastPage ? 'auto' : 'page'};">
        ${pageHeaderHtml}
        <table class="print-table">
          <thead>
            <tr>
              <th style="width: 6%; text-align: center; white-space: nowrap;">ลำดับ</th>
              <th style="width: 30%; text-align: left; white-space: nowrap;">รายการสินค้า / พัสดุ</th>
              <th style="width: 14%; text-align: left; white-space: nowrap;">ยี่ห้อ</th>
              <th style="width: 10%; text-align: center; white-space: nowrap;">ขนาด/สเปค</th>
              <th style="width: 10%; text-align: center; white-space: nowrap;">ล็อต</th>
              <th style="width: 10%; text-align: right; white-space: nowrap;">ยอดระบบ</th>
              <th style="width: 10%; text-align: center; white-space: nowrap;">ยอดนับจริง</th>
              <th style="width: 10%; text-align: right; white-space: nowrap;">ผลต่าง</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
        ${summaryFooterHtml}
      </div>
    `;
  });

  return fullHtml;
}
